import React, { useEffect, useRef, useState } from 'react';
import { Box, AspectRatio, useBreakpointValue, Text } from '@chakra-ui/react';
import Hls from 'hls.js';

interface VideoPlayerProps {
  videoUrl: string;
  title?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, title = 'Video Player' }) => {
  const marginBottom = useBreakpointValue({ base: 2, md: 4 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isYouTube, setIsYouTube] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);

  useEffect(() => {
    // Check if URL is a YouTube URL
    const getYouTubeVideoId = (url: string): string | null => {
      if (!url) return null;
      
      try {
        // Handle YouTube live URLs
        const liveRegExp = /^.*youtube\.com\/live\/([^\/?&]+)/;
        const liveMatch = url.match(liveRegExp);
        
        if (liveMatch && liveMatch[1]) {
          return liveMatch[1];
        }
        
        // Handle standard YouTube URLs
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        
        if (match && match[2] && match[2].length === 11) {
          return match[2];
        }
        
        return null;
      } catch (err) {
        console.error('Error parsing YouTube URL:', err);
        return null;
      }
    };

    const videoId = getYouTubeVideoId(videoUrl);
    
    if (videoId) {
      // It's a YouTube URL
      setIsYouTube(true);
      setYoutubeVideoId(videoId);
      setError(null);
    } else {
      // It's an HLS or other video URL
      setIsYouTube(false);
      setYoutubeVideoId(null);
      
      // Initialize HLS player
      const video = videoRef.current;
      if (!video) return;

      // Check if the URL is an HLS stream
      if (videoUrl.includes('.m3u8')) {
        if (Hls.isSupported()) {
          // Cleanup previous HLS instance
          if (hlsRef.current) {
            hlsRef.current.destroy();
          }

          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 90,
          });
          
          hlsRef.current = hls;
          hls.loadSource(videoUrl);
          hls.attachMedia(video);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play().catch(err => {
              console.error('Error auto-playing video:', err);
            });
          });
          
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS error:', data);
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  setError('Network error - attempting to recover');
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  setError('Media error - attempting to recover');
                  hls.recoverMediaError();
                  break;
                default:
                  setError('Fatal error - cannot play stream');
                  hls.destroy();
                  break;
              }
            }
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS support (Safari)
          video.src = videoUrl;
          video.addEventListener('loadedmetadata', () => {
            video.play().catch(err => {
              console.error('Error auto-playing video:', err);
            });
          });
        } else {
          setError('HLS is not supported in this browser');
        }
      } else {
        // Regular video URL
        video.src = videoUrl;
        video.play().catch(err => {
          console.error('Error auto-playing video:', err);
        });
      }
    }

    // Cleanup function
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl]);

  if (error) {
    return <Text color="red.500">{error}</Text>;
  }

  if (isYouTube && youtubeVideoId) {
    // Render YouTube embed
    const embedUrl = `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&rel=0`;
    
    return (
      <Box borderRadius="md" overflow="hidden" mb={marginBottom} width="100%">
        <AspectRatio ratio={16 / 9} maxW="100%">
          <iframe
            title={title}
            src={embedUrl}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </AspectRatio>
      </Box>
    );
  }

  // Render HLS video player
  return (
    <Box borderRadius="md" overflow="hidden" mb={marginBottom} width="100%">
      <AspectRatio ratio={16 / 9} maxW="100%">
        <video
          ref={videoRef}
          controls
          autoPlay
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
          }}
        />
      </AspectRatio>
    </Box>
  );
};
